import { ApiError } from "@/lib/api";
import { classifyNetworkFailure } from "./network-error";

/**
 * Stable backend error codes → playground i18n keys, so bn users don't see raw
 * English backend messages. Unmapped codes fall back to the server message.
 *
 * NOTE: PLAN_LIMIT_EXCEEDED maps to the AI-credits copy — only use this mapper
 * on run/explain paths; snippet-save handlers branch on `planLimit` first.
 */
const CODE_TO_MESSAGE_KEY: Readonly<Record<string, string>> = {
  RATE_LIMITED: "errorRateLimited",
  UPGRADE_REQUIRED: "errorUpgradeRequired",
  PLAN_LIMIT_EXCEEDED: "errorAiCredits",
  INSUFFICIENT_AI_CREDITS: "errorAiCredits",
  AI_UNAVAILABLE: "errorAiUnavailable",
  AI_ERROR: "errorAi",
  AI_GENERATION_IN_FLIGHT: "errorAi",
  AI_CONFIRMATION_PENDING: "errorAiConfirmationPending",
  AI_GENERATION_PRIOR_FAILED: "errorAiPriorFailed",
  AI_REPLAY_RESULT_UNAVAILABLE: "errorAiPriorFailed",
  PLAYGROUND_RUN_TIMEOUT: "errorJudgeTimeout",
  PLAYGROUND_RUN_UNAVAILABLE: "errorJudgeUnavailable",
  PLAYGROUND_RUN_REJECTED: "errorJudgeError",
  PLAYGROUND_RUN_FAILED: "errorJudgeError",
};

/**
 * A request that never got an answer (`PG-PWA01`): "offline" when the browser
 * knows it, "unreachable" otherwise. Checked before the code map because a
 * network failure is not an `ApiError` and used to fall through to a generic
 * "check your connection" that was also shown for a 500.
 */
function networkFailureKey(err: unknown): string | null {
  const kind = classifyNetworkFailure(err);
  if (kind === "offline") return "errorOffline";
  if (kind === "unreachable") return "errorUnreachable";
  return null;
}

export function playgroundErrorMessageKey(err: unknown): string | null {
  const network = networkFailureKey(err);
  if (network) return network;
  if (!(err instanceof ApiError) || !err.code) return null;
  return CODE_TO_MESSAGE_KEY[err.code] ?? null;
}

/**
 * The same codes, said for Fix My Error (`PG-AI02`).
 *
 * An overlay rather than a second full map: the copy above says "explanation"
 * because it was written for Explain My Code, and telling a stuck student that
 * "the explanation could not be generated" when they asked for a fix is the
 * kind of small wrongness nobody files a bug for and everybody notices. Codes
 * the overlay does not name fall through to the shared map.
 */
const FIX_CODE_TO_MESSAGE_KEY: Readonly<Record<string, string>> = {
  AI_UNAVAILABLE: "fixErrorUnavailable",
  AI_ERROR: "fixErrorFailed",
  AI_GENERATION_IN_FLIGHT: "fixErrorInFlight",
  AI_GENERATION_PRIOR_FAILED: "fixErrorPriorFailed",
  AI_REPLAY_RESULT_UNAVAILABLE: "fixErrorPriorFailed",
  AI_CONFIRMATION_PENDING: "fixErrorConfirmationPending",
  PLAYGROUND_FIX_NOT_ELIGIBLE: "fixErrorNotEligible",
  RATE_LIMITED: "fixErrorRateLimited",
  // `EX-HNT01`: the practice page's Fix My Error, while a contest or exam mounting the problem is open.
  JUDGE_PRACTICE_AI_LOCKED: "fixErrorLocked",
};

export function playgroundFixErrorMessageKey(err: unknown): string | null {
  const network = networkFailureKey(err);
  if (network) return network;
  if (!(err instanceof ApiError) || !err.code) return null;
  return FIX_CODE_TO_MESSAGE_KEY[err.code] ?? CODE_TO_MESSAGE_KEY[err.code] ?? null;
}
